import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { List } from 'immutable';
import { Observable } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { OnlineResourceGoogleBooks } from '®shared/bibliographic-resource';
import {
  GoogleBook,
  GoogleBooksResponse,
  GoogleBooksService,
} from '®shared/google-books';
import { cleanupIsbn } from '®shared/isbns/isbn-utils';
import {
  ListItemActionTypes,
  OnlineResourcesLoadedAction,
  RequestGoogleBooksAction,
} from '®shared/list-item/store';

@Injectable()
export class GoogleOnlineResourcesEffects {
  constructor(
    private actions$: Actions,
    private googleBooksService: GoogleBooksService,
  ) {}

  requestGoogleBooks$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ListItemActionTypes.REQUEST_GOOGLE_BOOKS),
      mergeMap((action: RequestGoogleBooksAction) => {
        const onlineResources = action.payload;
        const isbns = onlineResources
          .flatMap(onlineResource => onlineResource.isbns)
          .map(isbn => cleanupIsbn(isbn))
          .toSet()
          .toArray();

        return this.googleBooksService
          .getPreviewDetailsForIsbnsBatched(isbns)
          .pipe(
            filter(response => Object.keys(response).length > 0),
            map((previewsByIsbn: GoogleBooksResponse) => {
              return new OnlineResourcesLoadedAction(
                this.combineWithGoogleBooks(onlineResources, previewsByIsbn),
              );
            }),
          );
      }),
    ),
  );

  combineWithGoogleBooks(
    onlineResources: List<OnlineResourceGoogleBooks>,
    previewsByIsbn: GoogleBooksResponse,
  ): List<OnlineResourceGoogleBooks> {
    return onlineResources
      .map(onlineResource => {
        const preview: GoogleBook = this.googleBookForResource(
          onlineResource,
          previewsByIsbn,
        );

        return Object.assign({}, onlineResource, {
          preview_url: preview ? preview.preview_url : null,
        });
      })
      .filter(onlineResource => onlineResource.preview_url !== null);
  }

  googleBookForResource(
    onlineResource: OnlineResourceGoogleBooks,
    previewsByIsbn: GoogleBooksResponse,
  ): GoogleBook {
    return List(onlineResource.isbns)
      .map(isbn => previewsByIsbn[isbn])
      .find(preview => preview && preview.embeddable);
  }
}
